import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import ChangeLookupModal from './ChangeLookupModal';
import { fetchChangeRequestsForLookup } from '../../services/cabAgendaItemsService';
import { changeStateLabels } from '../../utils/labelMappings';
import type { LookupChangeRequest } from '../../types/cabAgendaItem';

const MIN_SEARCH_CHARS = 3;
const DEBOUNCE_MS = 300;
const EMPTY_RESULTS: LookupChangeRequest[] = [];

function formatChangeState(changeState?: string | null): string {
  if (!changeState) return '—';
  return changeStateLabels[changeState] || changeState;
}

interface ChangeRequestTaskFieldProps {
  changeRequestId: string;
  changeNumber: string;
  error?: string;
  onSelect: (change: LookupChangeRequest) => void;
  onClear: () => void;
  inputId?: string;
}

/**
 * Task field for CAB Agenda Item: type CR number for autocomplete, or use magnifying glass popup.
 * Both paths call onSelect with the same LookupChangeRequest shape (id + number + metadata).
 */
const ChangeRequestTaskField: React.FC<ChangeRequestTaskFieldProps> = ({
  changeRequestId,
  changeNumber,
  error,
  onSelect,
  onClear,
  inputId = 'agenda-task',
}) => {
  const [lookupOpen, setLookupOpen] = useState(false);
  const [query, setQuery] = useState(changeNumber || '');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const skipNextSearchRef = useRef(false);
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  useEffect(() => {
    // Sync display from committed selection only (avoid wiping in-progress typing)
    if (changeRequestId && changeNumber) {
      setQuery(changeNumber);
    }
  }, [changeRequestId, changeNumber]);

  useEffect(() => {
    if (skipNextSearchRef.current) {
      skipNextSearchRef.current = false;
      setDebouncedQuery('');
      return;
    }
    const trimmed = query.trim();
    if (trimmed.length < MIN_SEARCH_CHARS) {
      setDebouncedQuery('');
      return;
    }
    if (changeRequestId && trimmed.toLowerCase() === (changeNumber || '').trim().toLowerCase()) {
      setDebouncedQuery('');
      return;
    }
    const timer = window.setTimeout(() => {
      setDebouncedQuery(trimmed);
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [query, changeRequestId, changeNumber]);

  const { data, isFetching, isError } = useQuery({
    queryKey: ['lookup-changes', 'cab-agenda-autocomplete', debouncedQuery],
    queryFn: () =>
      fetchChangeRequestsForLookup({
        page: 1,
        pageSize: 10,
        search: debouncedQuery,
      }),
    enabled: debouncedQuery.length >= MIN_SEARCH_CHARS && dropdownOpen,
    staleTime: 15000,
  });

  const results = data?.items ?? EMPTY_RESULTS;

  useEffect(() => {
    const onDocClick = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const handleSelect = (change: LookupChangeRequest) => {
    skipNextSearchRef.current = true;
    setQuery(change.number);
    setDebouncedQuery('');
    setDropdownOpen(false);
    onSelect(change);
  };

  const handleClear = () => {
    setQuery('');
    setDebouncedQuery('');
    setDropdownOpen(false);
    onClear();
  };

  // Auto-select when autocomplete returns a single exact CR-number match
  useEffect(() => {
    if (!dropdownOpen || isFetching || changeRequestId || !data) return;
    const term = debouncedQuery.trim().toLowerCase();
    if (!term || results.length === 0) return;

    const exactMatches = results.filter(
      (item) => item.number.trim().toLowerCase() === term
    );
    const match =
      exactMatches.length === 1
        ? exactMatches[0]
        : results.length === 1 && results[0].number.trim().toLowerCase() === term
          ? results[0]
          : null;
    if (match) {
      skipNextSearchRef.current = true;
      setQuery(match.number);
      setDebouncedQuery('');
      setDropdownOpen(false);
      onSelectRef.current(match);
    }
  }, [debouncedQuery, results, isFetching, dropdownOpen, changeRequestId, data]);

  const showDropdown =
    dropdownOpen &&
    query.trim().length >= MIN_SEARCH_CHARS &&
    !(changeRequestId && query.trim().toLowerCase() === (changeNumber || '').trim().toLowerCase());

  return (
    <div ref={containerRef} className="relative">
      <div className="flex items-center gap-1">
        <div className="relative flex-1">
          <input
            id={inputId}
            type="text"
            value={query}
            autoComplete="off"
            placeholder="Select change request"
            onFocus={() => setDropdownOpen(true)}
            onChange={(e) => {
              const value = e.target.value;
              setQuery(value);
              setDropdownOpen(true);
              // Typing invalidates a prior selection until a CR is chosen again
              if (changeRequestId && value.trim() !== changeNumber) {
                onClear();
              }
            }}
            className={`w-full px-2 py-1.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 ${
              error ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-300'
            }`}
            aria-autocomplete="list"
            aria-controls={`${inputId}-results`}
          />
          {showDropdown && (
            <div
              id={`${inputId}-results`}
              role="listbox"
              className="absolute z-20 mt-1 w-full max-h-60 overflow-auto rounded-md border border-gray-200 bg-white shadow-lg"
            >
              {isFetching ? (
                <div className="px-3 py-2 text-sm text-gray-500">Searching...</div>
              ) : isError ? (
                <div className="px-3 py-2 text-sm text-red-600">
                  Unable to search Change Requests. Please try again.
                </div>
              ) : results.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500">No Change Requests found</div>
              ) : (
                results.map((change) => (
                  <button
                    key={change.id}
                    type="button"
                    role="option"
                    aria-selected={changeRequestId === change.id}
                    className="w-full text-left px-3 py-2 hover:bg-indigo-50 border-b border-gray-100 last:border-0"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleSelect(change)}
                  >
                    <div className="text-sm font-medium text-indigo-700">{change.number}</div>
                    <div className="text-xs text-gray-700 line-clamp-2">
                      {change.shortDescription || '—'}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      State: {formatChangeState(change.changeState)}
                    </div>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
        {(changeRequestId || query) && (
          <button
            type="button"
            onClick={handleClear}
            className="p-1.5 text-gray-400 hover:text-gray-600 border border-gray-300 rounded"
            aria-label="Clear task"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        )}
        <button
          type="button"
          onClick={() => setLookupOpen(true)}
          className="p-1.5 text-indigo-600 hover:text-indigo-800 border border-gray-300 rounded"
          aria-label="Lookup change request"
        >
          <MagnifyingGlassIcon className="h-4 w-4" />
        </button>
      </div>
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}

      <ChangeLookupModal
        isOpen={lookupOpen}
        onClose={() => setLookupOpen(false)}
        initialSelectedId={changeRequestId || null}
        initialSearch={query}
        onConfirm={handleSelect}
      />
    </div>
  );
};

export default ChangeRequestTaskField;
